/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ReactComponent as IconBuilding } from '../../../../shared/assets/Icons/IconBuilding.svg';
import { ReactComponent as Geolocate } from '../../../../shared/assets/geolocation.svg';
import { ReactComponent as IconServer } from '../../../../shared/assets/Icons/IconServer.svg';

// Design System Components
import GenericSearchbar from '../../../../ui-components/Searchbar/GenericSearchbar';
import { GenericTag } from '../../../../ui-components/Tag/GenericTag';
import { SkeletonLine } from '../../../../ui-components/Skeleton/GenericSkeleton';

// Data and Hooks
import { 
  SelectedEntity, 
  CloudInventory, 
  useCloudInventoryFiltering
} from '../BgpVisibilityData';

interface CloudInventorySidebarProps {
  cloudInventory: CloudInventory | null;
  onEntitySelect: (entity: SelectedEntity) => void;
  selectedEntity: SelectedEntity | null;
  /** When true, show skeleton instead of empty state (initial load) */
  isLoading?: boolean;
}

const CloudInventorySidebar: React.FC<CloudInventorySidebarProps> = ({
  cloudInventory,
  onEntitySelect,
  selectedEntity,
  isLoading = false
}) => {
  const { filteredCloudInventory } = useCloudInventoryFiltering(cloudInventory);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Auto-expand sections when data is loaded
  useEffect(() => {
    if (filteredCloudInventory && filteredCloudInventory.clouds.length > 0) {
      const newExpanded = new Set<string>();
      
      // Expand only parent elements (clouds)
      filteredCloudInventory.clouds.forEach((cloud) => {
        newExpanded.add(`cloud-${cloud.id}`);
      });
      
      setExpandedSections(newExpanded);
    }
  }, [filteredCloudInventory]);

  const handleEntityClick = (entity: any, type: 'reflector' | 'cluster', cloud: string, account: any, region: any) => {
    const selectedEntity: SelectedEntity = {
      cloud,
      account_id: account.id,
      account_display_name: account.name,
      region: region.name,
      type,
      id: entity.id,
      display_name: entity.display_name,
      subId: entity.subId || entity.sub_id // Include subId for reflectors
    };
    onEntitySelect(selectedEntity);
  };

  const isEntitySelected = (entityId: string, type: 'reflector' | 'cluster') => {
    return selectedEntity?.id === entityId && selectedEntity?.type === type;
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const isExpanded = (sectionId: string) => expandedSections.has(sectionId);

  if (!filteredCloudInventory) {
    if (isLoading) {
      return (
        <div className="h-full flex flex-col px-3 py-2.5 bg-semantic-bg-primary" role="status" aria-busy="true">
          <div className="mb-3">
            <SkeletonLine w="100%" h={36} className="rounded" />
          </div>
          <div className="space-y-3 flex-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="space-y-2">
                <SkeletonLine w="80%" h={20} className="rounded" />
                <SkeletonLine w="90%" h={16} className="rounded" />
                <SkeletonLine w="70%" h={16} className="rounded" />
              </div>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="h-full flex items-center justify-center bg-semantic-bg-primary">
        <div className="text-center text-semantic-text-secondary">
          <IconServer className="mx-auto mb-4 text-semantic-text-tertiary w-12 h-12" />
          <p>No cloud inventory data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-semantic-bg-primary border-t border-semantic-border-secondary overflow-hidden">
      {/* Search bar — full width; collapsing is handled by the divider handle on the page. */}
      <div className="px-3 py-2.5 bg-semantic-bg-primary flex-shrink-0">
        <GenericSearchbar
          placeholder="Search clouds, accounts, regions..."
          onFiltersChange={() => {
            // Search functionality will be handled by the useCloudInventoryFiltering hook
          }}
          liveFiltering={false}
          size="sm"
          inputMaxWidth="100%"
        />
      </div>

      {/* Cloud Inventory Content */}
      <div className="flex-1 overflow-y-auto bg-semantic-bg-primary pb-8 min-h-0">
        {filteredCloudInventory.clouds.map((cloud) => (
          <div key={cloud.id} >
            {/* Cloud Provider Header — compact section label for the group */}
            <div
              className="px-3 py-2 cursor-pointer"
              onClick={() => toggleSection(`cloud-${cloud.id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-body-small font-medium uppercase tracking-wide text-semantic-text-tertiary">
                    {cloud.name}
                  </span>
                </div>
                {isExpanded(`cloud-${cloud.id}`) ? (
                  <ChevronDown size={16} className="text-semantic-text-secondary" />
                ) : (
                  <ChevronRight size={16} className="text-semantic-text-secondary" />
                )}
              </div>
            </div>

            {/* Cloud Content */}
            {isExpanded(`cloud-${cloud.id}`) && (
              <div className="bg-semantic-bg-primary">
                {cloud.accounts.map((account) => (
                  <div key={account.id} >
                    {/* Account Header */}
                    <div
                      className="px-4 py-2 cursor-pointer"
                      onClick={() => toggleSection(`account-${account.id}`)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex justify-start items-center space-x-2 min-w-0">
                          <IconBuilding  className="text-semantic-text-secondary w-5 h-5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="truncate text-body text-semantic-text-secondary" title={account.name}>{account.name}</div>
                            <div className="truncate text-body-small text-semantic-text-secondary" title={account.id}>{account.id}</div>
                          </div>
                        </div>
                        {isExpanded(`account-${account.id}`) ? (
                          <ChevronDown size={14} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={14} className="text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Account Content */}
                    {isExpanded(`account-${account.id}`) && (
                      <div className="">
                        {account.regions.map((region) => (
                          <div key={region.id} >
                            {/* Region Header */}
                            <div
                              className="px-6 py-1.5 cursor-pointer"
                              onClick={() => toggleSection(`region-${region.id}`)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <Geolocate  className="text-semantic-text-secondary w-4 h-4" />
                                  <span className="text-body text-semantic-text-secondary">{region.name}</span>
                                </div>
                                {isExpanded(`region-${region.id}`) ? (
                                  <ChevronDown size={12} className="text-semantic-text-secondary" />
                                ) : (
                                  <ChevronRight size={12} className="text-semantic-text-secondary" />
                                )}
                              </div>
                            </div>

                            {/* Region Content */}
                            {isExpanded(`region-${region.id}`) && (
                              <div className="bg-semantic-bg-primary px-6 py-1 space-y-0.5">
                                {/* Reflectors */}
                                {region.reflectors.map((reflector) => (
                                  <div
                                    key={reflector.id}
                                    onClick={() => handleEntityClick(reflector, 'reflector', cloud.name, account, region)}
                                    className={`px-2 py-1.5 rounded cursor-pointer transition-colors ${
                                      isEntitySelected(reflector.id, 'reflector')
                                        ? 'bg-semantic-bg-brand-primary border-l-2 border-semantic-outline-primary text-semantic-text-brand'
                                        : 'hover:bg-semantic-state-active'
                                    }`}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <IconServer className="text-semantic-text-secondary w-4 h-4 flex-shrink-0" />
                                      {/* Truncated, not wrapped: the id is a full UUID and would
                                          otherwise spill onto a third line in every row. */}
                                      <div className="flex-1 min-w-0">
                                        <div className="truncate text-body text-semantic-text-secondary" title={reflector.display_name}>{reflector.display_name}</div>
                                        <div className="truncate text-body-xsmall text-semantic-text-secondary" title={reflector.id}>{reflector.id}</div>
                                      </div>
                                      <GenericTag
                                        label={reflector.status}
                                        variant={reflector.status === 'online' ? 'success' : reflector.status === 'degraded' ? 'warning' : 'error'}
                                        size="small"
                                      />
                                    </div>
                                  </div>
                                ))}

                                {/* Clusters */}
                                {region.clusters.map((cluster) => (
                                  <div
                                    key={cluster.id}
                                    onClick={() => handleEntityClick(cluster, 'cluster', cloud.name, account, region)}
                                    className={`px-2 py-1.5 rounded cursor-pointer transition-colors ${
                                      isEntitySelected(cluster.id, 'cluster')
                                        ? 'bg-semantic-bg-brand-primary border-l-2 border-semantic-outline-primary'
                                        : 'hover:bg-semantic-state-active'
                                    }`}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <IconBuilding  className="text-semantic-text-secondary w-5 h-5 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="truncate text-body text-semantic-text-secondary" title={cluster.display_name}>{cluster.display_name}</div>
                                      </div>
                                      <GenericTag
                                        label={cluster.health}
                                        variant={cluster.health === 'Healthy' ? 'success' : cluster.health === 'Degraded' ? 'warning' : 'error'}
                                        size="small"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CloudInventorySidebar;
